import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { SubscriptionCollector } from '@draeger-academy/common/lib/decorators/subscription-collector.decorator';
import { PathsService } from '@draeger-academy/common/lib/services/paths/paths.service';
import { TranslateService } from '@ngx-translate/core';
import { CartItem } from 'models/cartItem';
import { BehaviorSubject, Observable } from 'rxjs';
import { Course } from '../../../../../models/course';
import { ConfigurationService } from '../configuration/configuration.service';

export interface IProductCategories {
    Name: string;
    DisplayName: string;
}

interface BreadcrumbLinksInterface {
    link: string;
    label: string;
    showText: boolean;
    forceLink?: boolean;
}

@Injectable()
export class BreadcrumbNavigationService {
    public navigationData: any;
    private _$breadcrumb: BehaviorSubject<BreadcrumbLinksInterface[]> = new BehaviorSubject(null);
    private _currentUrl: string;
    private _isHomepage: boolean = false;
    private _isTermsPage: boolean = false;
    private _isDataProtectionPage: boolean = false;
    private _isContactPage: boolean = false;
    private _isImprintPage: boolean = false;
    private _$translations: BehaviorSubject<any> = new BehaviorSubject({});

    @SubscriptionCollector()
    private subs;
    private _breadcrumbItems: BreadcrumbLinksInterface[] = [];
    private _isLegalNotice: boolean;
    private _isSupplierIndentification: boolean;
    private _isLicenses: boolean;
    private _isTtransparency: boolean;
    private _isSearch: boolean;
    private _isForgotPassword: boolean;
    private _isChangePassword: boolean;
    private _isResetPassword: boolean;
    private _isPersonalData: boolean;
    private _isLogin: boolean;
    private _isLanding: boolean;
    private _isMyCourses: boolean;
    private _isOverviewTrainings: boolean;
    private _isTrainingAdvisor: boolean;
    private portal: string = null;

    constructor(
        private _configService: ConfigurationService,
        private _titleService: Title,
        private _translateService: TranslateService,
        private _pathsService: PathsService
    ) {}

    public get breadcrumb$(): Observable<BreadcrumbLinksInterface[]> {
        return this._$breadcrumb.asObservable();
    }

    private get _is404Page(): boolean {
        const _pageTitle: string = this._titleService.getTitle();
        return _pageTitle.indexOf('404') > -1;
    }

    public initPartnerBreadcrumb(portalId: string): void {
        this._translateService.stream(['pageTypeLinkLabel']).subscribe((data: any) => {
            this._$translations.next(data.pageTypeLinkLabel);
            this._breadcrumbItems = [];
            const _label = this._$translations.getValue().home ? this._$translations.getValue().home : 'Home';
            if (portalId) this._addItemToBreadcrumb('/partner/' + portalId, _label, false);
            this._$breadcrumb.next(this._breadcrumbItems);
            this._buildBreadcrumb();
        });
    }

    public initBreadcrumb(pageType?: string): void {
        const _pageType = pageType ? pageType : '';
        this._currentUrl = window.location.pathname.toLowerCase();
        this._isHomepage = _pageType.toLowerCase() === 'home';
        this._isTermsPage = _pageType.toLowerCase() === 'terms';
        this._isDataProtectionPage = _pageType.toLowerCase() === 'data-protection';
        this._isContactPage = _pageType.toLowerCase() === 'contact-us';
        this._isImprintPage = _pageType.toLowerCase() === 'imprint';

        this._isLegalNotice = _pageType.toLowerCase() === 'legalnotice';
        this._isSupplierIndentification = _pageType.toLowerCase() === 'supplierindentification';
        this._isLicenses = _pageType.toLowerCase() === 'licenses';
        this._isTtransparency = _pageType.toLowerCase() === 'transparency';
        this._isSearch = _pageType.toLowerCase() === 'search';

        this._isLogin = _pageType.toLowerCase() === 'login';
        this._isForgotPassword = _pageType.toLowerCase() === 'forgot-password';
        this._isChangePassword = _pageType.toLowerCase() === 'change-password';
        this._isResetPassword = _pageType.toLowerCase() === 'reset-password';
        this._isPersonalData = _pageType.toLowerCase() === 'personal-data';
        this._isLanding = _pageType.toLowerCase() === 'landing';
        this._isMyCourses = _pageType.toLowerCase() === 'my-courses';

        this._isOverviewTrainings = _pageType.toLocaleLowerCase() === 'checkout';
        this._isTrainingAdvisor = _pageType.toLocaleLowerCase() === 'training-advisor';

        if (this._configService.isPartnerPage()) {
            this._configService.currentPortal$.subscribe((portal) => {
                if (portal) {
                    this.portal = portal;
                }
            });
        } else {
        }
        this.subs = this._translateService.get('pageTypeLinkLabel').subscribe((data) => {
            this._$translations.next(data);
            this._buildBreadcrumb();
        });
    }

    ngOnDestroy() {
        // do not delete, needed for subscription collector
    }

    public resetBreadcrumb(): void {
        this.ngOnDestroy();
        this._breadcrumbItems = [];
        this._$breadcrumb.next([]);
    }

    public setCoursesBreadcrumb() {
        this._buildHomeCrumb();
        this._addItemToBreadcrumb('/courses', this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses');

        this._$breadcrumb.next(this._breadcrumbItems);
    }

    public setInitialCourseBreadcrumb() {
        this._buildHomeCrumb();
        this._addItemToBreadcrumb('/courses', this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses');
        this._$breadcrumb.next(this._breadcrumbItems);
    }

    public updateCourseBreadcrumb(course?: Course, count?: number) {
        this._breadcrumbItems = [];
        this._buildHomeCrumb();
        if (this.portal === null) {
            if (!course) {
                this._addItemToBreadcrumb(
                    '/courses',
                    this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses'
                );
            }
            if (count > 1) {
                this._addItemToBreadcrumb(
                    '/courses',
                    this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses'
                );
            }
        }
        if (course && course.id) {
            this._addItemToBreadcrumb('/course/' + course.id, course.name);
        }

        this._$breadcrumb.next(this._breadcrumbItems);
    }

    public setEventBreadcrumb(event?: CartItem, count?: number) {
        this._buildHomeCrumb();
        if (this.portal === null) {
            if (!event) {
                this._addItemToBreadcrumb(
                    '/courses',
                    this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses'
                );
            }
            if (count > 1) {
                this._addItemToBreadcrumb(
                    '/courses',
                    this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses'
                );
            }
        }
        if (event && event.id) {
            if (this.portal === null) {
                this._addItemToBreadcrumb('/course/' + event.courseId, event.courseName);
            } else {
                this._addItemToBreadcrumb('/partner/' + this.portal + '/course-detail/' + event.courseId, event.courseName);
            }
        }
        let date = new Date(event.beginDateTime);
        this._addItemToBreadcrumb('/event/' + event.id, 'Event ' + date.toLocaleDateString());
        this._$breadcrumb.next(this._breadcrumbItems);
    }

    public setBookingBreadcrumb(course?: Course, count?: number) {
        this._buildHomeCrumb();

        if (count > 1) {
            this._addItemToBreadcrumb(
                '/courses',
                this._$translations.getValue().courses ? this._$translations.getValue().courses : 'Courses'
            );
        }
        if (course && course.id) {
            this._addItemToBreadcrumb('/course/' + course.id, course.name);
            this._addItemToBreadcrumb(
                '/booking/' + course.id,
                this._$translations.getValue().booking ? this._$translations.getValue().booking : 'Booking'
            );
        }
        this._$breadcrumb.next(this._breadcrumbItems);
    }

    private _buildBreadcrumb(): void {
        this._breadcrumbItems = [];
        if (this._isHomepage) {
            this._buildHomeCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isContactPage) {
            this._buildContactCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isTermsPage) {
            this._buildTermsCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isDataProtectionPage) {
            this._buildDataProtectionCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isImprintPage) {
            this._buildImprintCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isLicenses) {
            this._buildLicensesCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isSupplierIndentification) {
            this._buildSupplierIndentificationCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isTtransparency) {
            this._buildTransparencyCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isSearch) {
            this._buildSearchCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isLegalNotice) {
            this._buildLegalNoticeCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isLogin) {
            this._buildLoginCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isForgotPassword) {
            this._buildForgotPasswordCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isChangePassword) {
            this._buildChangePasswordCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isResetPassword) {
            this._buildResetPasswordCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isPersonalData) {
            this._buildPersonalDataCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isLanding) {
            this._buildLandingCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isMyCourses) {
            this._buildMyCoursesCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isOverviewTrainings) {
            this.__buildOverviewTrainingsCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else if (this._isTrainingAdvisor) {
            this.__buildTrainingAdvisorCrumb();
            this._$breadcrumb.next(this._breadcrumbItems);
        } else {
            setTimeout(() => {
                if (this._is404Page) {
                    this._buildHomeCrumb();
                    this._$breadcrumb.next(this._breadcrumbItems);
                }
            });
        }
    }

    private _addItemToBreadcrumb(url: string, label: string, forceLink?: boolean): void {
        if (label !== '' && url !== '') {
            this._breadcrumbItems.push({
                link: url,
                label: label,
                showText: true,
                forceLink: typeof forceLink !== 'undefined' ? forceLink : !this._isHomepage && this._breadcrumbItems.length === 0
            });
        }
    }

    private _buildHomeCrumb(): void {
        if (this.portal) {
            this._buildSingleBreadcrumb(
                this._$translations.getValue().home ? this._$translations.getValue().home : 'Home',
                this._pathsService.getHomeLink()
            );
        } else {
            this._buildSingleBreadcrumb(this._$translations.getValue().home ? this._$translations.getValue().home : 'Home');
        }
    }

    private _buildContactCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().contact ? this._$translations.getValue().contact : 'Contact us');
    }

    private _buildTermsCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().terms ? this._$translations.getValue().terms : 'General Terms');
    }

    private _buildLicensesCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().licenses ? this._$translations.getValue().licenses : 'Licenses');
    }

    private _buildTransparencyCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().transparency ? this._$translations.getValue().transparency : 'Transparency'
        );
    }

    private _buildSupplierIndentificationCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().supplierIndentification
                ? this._$translations.getValue().supplierIndentification
                : 'supplier-indentification'
        );
    }

    private _buildLegalNoticeCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().legalNotice ? this._$translations.getValue().legalNotice : 'Legal Notice'
        );
    }

    private _buildSearchCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().searchPage ? this._$translations.getValue().searchPage : 'Search');
    }

    private _buildLoginCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().login ? this._$translations.getValue().login : 'Login');
    }

    private _buildForgotPasswordCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().forgotPassword ? this._$translations.getValue().forgotPassword : 'Forgot Password'
        );
    }

    private _buildChangePasswordCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().changePassword ? this._$translations.getValue().changePassword : 'change-password'
        );
    }

    private _buildResetPasswordCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().resetPassword ? this._$translations.getValue().resetPassword : 'Reset Password'
        );
    }

    private _buildPersonalDataCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().personalData ? this._$translations.getValue().personalData : 'Personal Data'
        );
    }

    private _buildLandingCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().landing ? this._$translations.getValue().landing : 'Landing');
    }

    private _buildDataProtectionCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().dataProtection
                ? this._$translations.getValue().dataProtection
                : 'breadcrumb translation missing: Data Protection'
        );
    }

    private _buildImprintCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().imprint ? this._$translations.getValue().imprint : 'Imprint');
    }

    private _buildMyCoursesCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(this._$translations.getValue().myCourses ? this._$translations.getValue().myCourses : 'my-courses');
    }

    private __buildOverviewTrainingsCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().overviewTrainings ? this._$translations.getValue().overviewTrainings : 'Overview Trainings'
        );
    }

    private __buildTrainingAdvisorCrumb(): void {
        this._buildHomeCrumb();
        this._buildSingleBreadcrumb(
            this._$translations.getValue().trainingAdvisor ? this._$translations.getValue().trainingAdvisor : 'Training Advisor'
        );
    }

    private _buildSingleBreadcrumb(label: string, path?: string): void {
        const _label = label;
        if (label) this._addItemToBreadcrumb('/' + (path ? path.toLowerCase() : label.toLowerCase()), _label);
    }

    initPartnerLegalBreadcrumb(portalId: string, legalPageElement: any) {
        this._breadcrumbItems = [];
        const _label = this._$translations.getValue().home ? this._$translations.getValue().home : 'Home';
        if (portalId) this._addItemToBreadcrumb('/partner/' + portalId, _label, true);
        if (legalPageElement && legalPageElement.label) {
            this._addItemToBreadcrumb('/partner/' + portalId + '/' + legalPageElement.link, legalPageElement.label, false);
        }
        this._$breadcrumb.next(this._breadcrumbItems);
    }

    setAccountBreadcrumb(subPage?: string) {
        this._translateService.stream(['pageTypeLinkLabel']).subscribe((data: any) => {
            this._$translations.next(data.pageTypeLinkLabel);
            this._breadcrumbItems = [];
            this._buildHomeCrumb();
            const _label = this._$translations.getValue().account ? this._$translations.getValue().account : 'Mein Konto';
            this._buildSingleBreadcrumb(_label, this._pathsService.getAccountLink());
            this._$breadcrumb.next(this._breadcrumbItems);
            this._buildBreadcrumb();
        });
    }
}
